import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router-dom";
import Login from "../pages/login";
import PrivateRoute from "../utils/private-router";
import React, { lazy } from "react";
import PageLoading from "../components/pageLoading/pageLoading";
import NotFound from "../components/not-found";
import TrainingRequest from "../pages/training-request";
import Dealership from "../sections/setting/dealership";
import BusinessHours from "../sections/setting/business-hours";
import Lead from "../sections/setting/lead";
import Language from "../sections/setting/language";

// Lazy load the components
const LazyInbox = lazy(() => import("../sections/dashboard/inbox"));
const LazyHelpCenter = lazy(() => import("../pages/help-center/help-center"));
const LazySupport = lazy(() => import("../pages/support/support"));
const LazyVideosList = lazy(() => import("../pages/help-center/videos-list"));
const LazyVideosPage = lazy(() => import("../pages/help-center/video-page"));
const LazyHolidays = lazy(() => import("../sections/setting/holidays"));
const LazyTechnicalQuestions = lazy(
  () => import("../sections/setting/technical-questions/technical-questions")
);
const LazyAISetting = lazy(() => import("../sections/setting/ai-setting-inputs"));
const LazySpecialRules = lazy(() => import("../sections/setting/special-rules"));
const LazySocialMedia = lazy(() => import("../sections/setting/social-media/social-media"));
const LazyUserManagement = lazy(() => import("../sections/setting/user-management"));
const LazyCustomFollowUp = lazy(
  () => import("../sections/setting/custom-follow-up/custom-follow-up")
);
const LazyEmailMessage = lazy(() => import("../sections/setting/email"));
const LazyEmailSignature = lazy(() => import("../sections/setting/email-signature"));
const LazySMSMessage = lazy(() => import("../sections/setting/sms"));
const LazyAppointment = lazy(() => import("../pages/appointment"));
const LazyReport = lazy(() => import("../pages/report"));
const LazyCampaign = lazy(() => import("../pages/campain/campaign"));
const LazyCampaignDetails = lazy(() => import("../pages/campain/campaign-details"));
const LazyCreateCampaign = lazy(() => import("../pages/campain/create-campaign"));
const LazyCampaignList = lazy(() => import("../pages/campain/campaign-list"));
const LazySetting = lazy(() => import("../pages/setting"));
const LazyCars = lazy(() => import("../pages/cars/cars"));
const LazyTasks = lazy(() => import("../pages/task/tasks"));
const LazyReviewMax = lazy(() => import("../pages/review-max/review-max"));
const LazyReviewMaxWhatsNew = lazy(() => import("../pages/review-max/whats-new"));
const LazyReviewMaxTutorials = lazy(() => import("../pages/review-max/tutorials"));
const LazyReviewMaxFeedback = lazy(() => import("../pages/review-max/feedback"));
const LazyAutoMarketing = lazy(() => import("../pages/auto-marketing/auto-marketing"));
const LazyWebsiteAnalytics = lazy(() => import("../pages/website-analytics"));
const LazyCommunicationReport = lazy(() => import("../pages/communication-report"));
const LazySofiaV2 = lazy(() => import("../pages/sofia-v2"));
const LazyKnowledgeBase = lazy(() => import("../pages/knowledge-base/knowledge-base"));

// Facebook Marketplace section
const LazyFacebookLayout = lazy(() => import("../pages/facebook/facebook-layout"));
const LazyFacebookDashboard = lazy(() => import("../pages/facebook/dashboard"));
const LazyFacebookAds = lazy(() => import("../pages/facebook/ads"));
const LazyFacebookAdDetail = lazy(() => import("../pages/facebook/ad-detail"));
const LazyFacebookTemplates = lazy(() => import("../pages/facebook/templates"));
const LazyFacebookLeads = lazy(() => import("../pages/facebook/leads"));
const LazyFacebookChatbot = lazy(() => import("../pages/facebook/chatbot"));
const LazyFacebookSettings = lazy(() => import("../pages/facebook/settings"));

// Craigslist section
const LazyCraigslistLayout = lazy(() => import("../pages/craigslist/craigslist-layout"));
const LazyCraigslistOverview = lazy(() => import("../pages/craigslist/overview"));
const LazyCraigslistInventory = lazy(() => import("../pages/craigslist/inventory"));
const LazyCraigslistAds = lazy(() => import("../pages/craigslist/ads"));
const LazyCraigslistCampaigns = lazy(() => import("../pages/craigslist/campaigns"));
const LazyCraigslistCampaignDetail = lazy(() => import("../pages/craigslist/campaign-detail"));
const LazyCraigslistRunDetail = lazy(() => import("../pages/craigslist/run-detail"));
const LazyCraigslistReports = lazy(() => import("../pages/craigslist/reports"));
const LazyCraigslistPayments = lazy(() => import("../pages/craigslist/payments"));

// Create the router
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<NotFound />} path="" element={<PrivateRoute />}>
      <Route
        index
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyInbox />
          </React.Suspense>
        }
      />
      <Route
        path=":id"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyInbox />
          </React.Suspense>
        }
      >
        <Route
          path="schedule-appointment"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyInbox />
            </React.Suspense>
          }
        />
      </Route>
      <Route
        path="appointment"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyAppointment />
          </React.Suspense>
        }
      />
      <Route
        path="report"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyReport />
          </React.Suspense>
        }
      />
      <Route
        path="support"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazySupport />
          </React.Suspense>
        }
      />
      <Route
        path="help-center"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyHelpCenter />
          </React.Suspense>
        }
      >
        <Route path="" element={<LazyVideosList />} />
        <Route path=":title" element={<LazyVideosPage />} />
      </Route>
      <Route
        path="campaign"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyCampaign />
          </React.Suspense>
        }
      >
        <Route path="create" element={<LazyCreateCampaign />} />
        <Route path="list" element={<LazyCampaignList />} />
        <Route path="edit/:id" element={<LazyCampaignDetails />} />
        <Route path=":id" element={<LazyCampaignDetails />} />
        <Route path="" element={<LazyCampaignList />} />
      </Route>
      <Route
        path="training-request"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <TrainingRequest />
          </React.Suspense>
        }
      />
      <Route
        path="cars"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyCars />
          </React.Suspense>
        }
      />
      <Route
        path="task"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyTasks />
          </React.Suspense>
        }
      />
      <Route
        path="review-max"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyReviewMax />
          </React.Suspense>
        }
      />
      <Route
        path="review-max-whats-new"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyReviewMaxWhatsNew />
          </React.Suspense>
        }
      />
      <Route
        path="review-max-tutorials"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyReviewMaxTutorials />
          </React.Suspense>
        }
      />
      <Route
        path="review-max-feedback"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyReviewMaxFeedback />
          </React.Suspense>
        }
      />
      <Route
        path="auto-marketing"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyAutoMarketing />
          </React.Suspense>
        }
      />
      <Route
        path="website-analytics"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyWebsiteAnalytics />
          </React.Suspense>
        }
      />
      <Route
        path="communication-report"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyCommunicationReport />
          </React.Suspense>
        }
      />
      <Route
        path="Sofia-v2"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazySofiaV2 />
          </React.Suspense>
        }
      />
      <Route
        path="knowledge-base"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyKnowledgeBase />
          </React.Suspense>
        }
      />
      <Route
        path="Facebook"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyFacebookLayout />
          </React.Suspense>
        }
      >
        <Route index element={<Navigate to="Dashboard" replace />} />
        <Route
          path="Dashboard"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookDashboard />
            </React.Suspense>
          }
        />
        <Route
          path="Ads"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookAds />
            </React.Suspense>
          }
        />
        <Route
          path="Ads/:vin"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookAdDetail />
            </React.Suspense>
          }
        />
        <Route
          path="Templates"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookTemplates />
            </React.Suspense>
          }
        />
        <Route
          path="Leads"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookLeads />
            </React.Suspense>
          }
        />
        <Route
          path="FBChatbot"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookChatbot />
            </React.Suspense>
          }
        />
        <Route
          path="Settings"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyFacebookSettings />
            </React.Suspense>
          }
        />
      </Route>
      <Route
        path="Craigslist"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazyCraigslistLayout />
          </React.Suspense>
        }
      >
        <Route index element={<Navigate to="Overview" replace />} />
        <Route
          path="Overview"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistOverview />
            </React.Suspense>
          }
        />
        <Route
          path="Inventory"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistInventory />
            </React.Suspense>
          }
        />
        <Route
          path="Ads"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistAds />
            </React.Suspense>
          }
        />
        <Route
          path="Campaigns"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistCampaigns />
            </React.Suspense>
          }
        />
        <Route
          path="Campaigns/:campaignId"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistCampaignDetail />
            </React.Suspense>
          }
        />
        <Route
          path="Campaigns/:campaignId/runs/:runId"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistRunDetail />
            </React.Suspense>
          }
        />
        <Route
          path="Reports"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistReports />
            </React.Suspense>
          }
        />
        <Route
          path="Payments"
          element={
            <React.Suspense fallback={<PageLoading />}>
              <LazyCraigslistPayments />
            </React.Suspense>
          }
        />
      </Route>
      <Route
        path="setting"
        element={
          <React.Suspense fallback={<PageLoading />}>
            <LazySetting />
          </React.Suspense>
        }
      >
        <Route path="" element={<Navigate to="dealership" replace />} />
        <Route path="dealership" element={<Dealership />} />
        <Route path="business-hours" element={<BusinessHours />} />
        <Route path="lead" element={<Lead />} />
        <Route path="language" element={<Language />} />
        <Route path="holidays" element={<LazyHolidays />} />
        <Route path="AI-message" element={<LazyAISetting />} />

        {/* <Route path="AI-inactive-hours" element={<LazyAIInactiveHours />} /> */}

        <Route path="technical-questions" element={<LazyTechnicalQuestions />} />
        <Route path="special-rules" element={<LazySpecialRules />} />
        <Route path="custom-follow-up" element={<LazyCustomFollowUp />} />
        <Route path="social-media" element={<LazySocialMedia />} />
        <Route path="user-management" element={<LazyUserManagement />} />
        <Route path="email-message" element={<LazyEmailMessage />} />
        <Route path="email-signature" element={<LazyEmailSignature />} />
        <Route path="sms-message" element={<LazySMSMessage />} />
        <Route path="*" element={<Dealership />} />
      </Route>
      <Route
        path="login"
        // element={<LazyLogin />}
        element={
          <React.Suspense fallback={<PageLoading />}>
            <Login />
          </React.Suspense>
        }
      />
      {/* Place the catch-all route at the end */}
      <Route path="*" element={<NotFound />} />
    </Route>
  )
);

export default router;
